import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Calendar, User, Users, UserCog, Send, ShoppingCart, Settings, LogOut, Star, Package, Menu, X, Info, Mail, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Navbar = () => {
  const { user, logout, isAdmin, isGroomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [employeePermissions, setEmployeePermissions] = useState({});
  const [branding, setBranding] = useState({
    siteName: 'Dog Grooming',
    siteIcon: '🐕',
    logoType: 'emoji',
    logoImage: null,
    logoTextColor: '#1f2937',
    logoBackgroundColor: '#2563eb',
    logoFontFamily: 'system-ui',
    navbarBackgroundColor: '#ffffff',
    navLabels: {
      home: 'Home',
      clients: 'Clients',
      appointments: 'Appointments',
      availability: 'Availability',
      timeOff: 'Time Off',
      recommendations: 'Recommendations',
      profile: 'Profile',
      users: 'Employees',
      messaging: 'Messaging',
      quickBuy: 'Quick Buy',
      inventory: 'Inventory',
      aboutUs: 'About Us',
      contactUs: 'Contact Us',
      admin: 'Admin',
    },
    navLabelStyles: {},
    navOrder: {
      admin: ['home', 'clients', 'book', 'appointments', 'availability', 'timeOff', 'recommendations', 'profile', 'users', 'messaging', 'quickBuy', 'inventory', 'aboutUs', 'contactUs', 'admin'],
      employee: ['home', 'clients', 'book', 'appointments', 'availability', 'timeOff', 'myHours', 'recommendations', 'profile', 'quickBuy', 'aboutUs', 'contactUs'],
      client: ['home', 'clients', 'appointments', 'availability', 'recommendations', 'profile', 'aboutUs', 'contactUs'],
    },
    navStyle: 'auto',
  });

  // Close menu when navigating
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadBranding();
    loadEmployeePermissions();
    
    // Listen for branding update events
    const handleBrandingUpdate = () => {
      console.log('Branding update event received, reloading...');
      loadBranding();
    };
    
    // Listen for permissions update events
    const handlePermissionsUpdate = () => {
      console.log('Permissions update event received, reloading...');
      loadEmployeePermissions();
    };
    
    window.addEventListener('brandingUpdated', handleBrandingUpdate);
    window.addEventListener('permissionsUpdated', handlePermissionsUpdate);
    
    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdate);
    };
  }, []);

  const loadBranding = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/site_branding`);
      if (response.data.info_value) {
        const savedBranding = JSON.parse(response.data.info_value);
        // Merge saved branding with defaults to ensure new fields are included
        setBranding(prev => ({
          ...prev,
          ...savedBranding,
          navLabels: {
            ...prev.navLabels,
            ...savedBranding.navLabels,
          },
          navLabelStyles: {
            ...prev.navLabelStyles,
            ...savedBranding.navLabelStyles,
          },
          navOrder: savedBranding.navOrder || prev.navOrder,
        }));
      }
    } catch (error) {
      // Keep defaults if not found
    }
  };

  const loadEmployeePermissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/employee_permissions`);
      if (response.data.info_value) {
        const data = JSON.parse(response.data.info_value);
        setEmployeePermissions(data);
      }
    } catch (error) {
      // Keep empty permissions if not found
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to get nav label styles
  const getNavLabelStyle = (labelKey) => ({
    color: branding.navLabelStyles?.[labelKey]?.color || '#374151',
    fontFamily: branding.navLabelStyles?.[labelKey]?.fontFamily || 'system-ui'
  });

  // Helper function to get nav item config
  const getNavItemConfig = (key) => {
    // Check if employee has permission for Quick Buy
    const hasQuickBuyPermission = () => {
      if (isAdmin) return true; // Admins always have access
      if (!isGroomer && !user) return false; // Clients don't have access
      
      // Check global setting first
      const globalEnabled = employeePermissions?.global?.quickBuyEnabled !== false;
      if (!globalEnabled) return false;
      
      // Then check individual permission
      return employeePermissions?.individual?.[user?.user_id]?.quickBuy || false;
    };

    const configs = {
      home: { to: '/', icon: Home, label: branding.navLabels.home, show: true },
      clients: { 
        to: !isAdmin && !isGroomer ? '/book' : '/clients', 
        icon: !isAdmin && !isGroomer ? Calendar : Users, 
        label: !isAdmin && !isGroomer ? 'Book' : branding.navLabels.clients, 
        show: true 
      },
      appointments: { to: '/appointments', icon: Calendar, label: branding.navLabels.appointments, show: true },
      book: { to: '/book', icon: Calendar, label: 'Book Now', show: isAdmin || isGroomer },
      availability: { to: '/availability', icon: Calendar, label: branding.navLabels.availability, show: true },
      timeOff: { to: '/time-off', icon: Calendar, label: branding.navLabels.timeOff, show: isAdmin || isGroomer },
      // Roadmap item #17 Phase 2: groomer-only "My Hours" page. Always
      // visible to groomers; the page itself shows a read-only banner
      // when the admin hasn't enabled self-scheduling, so we don't need
      // to fetch the setting here just to gate the link.
      myHours: { to: '/my-hours', icon: Clock, label: branding.navLabels.myHours || 'My Hours', show: isGroomer },
      recommendations: { to: '/recommendations', icon: Star, label: branding.navLabels.recommendations, show: true },
      profile: { to: '/profile', icon: User, label: branding.navLabels.profile, show: true },
      users: { to: '/users', icon: UserCog, label: branding.navLabels.users, show: isAdmin },
      messaging: { to: '/messaging', icon: Send, label: branding.navLabels.messaging, show: isAdmin },
      quickBuy: { to: '/quick-buy', icon: ShoppingCart, label: branding.navLabels.quickBuy, show: hasQuickBuyPermission() },
      inventory: { to: '/inventory', icon: Package, label: branding.navLabels.inventory, show: isAdmin },
      aboutUs: { to: '/about', icon: Info, label: branding.navLabels.aboutUs || 'About Us', show: true },
      contactUs: { to: '/contact', icon: Mail, label: branding.navLabels.contactUs || 'Contact Us', show: true },
      admin: { to: '/admin', icon: Settings, label: branding.navLabels.admin, show: isAdmin },
    };
    return configs[key];
  };

  if (!user) return null;

  // Determine if hamburger mode should be used
  const useHamburger = branding.navStyle === 'hamburger' || branding.navStyle === 'auto';
  const forceHorizontal = branding.navStyle === 'horizontal';

  // Determine which navbar order to use based on user role
  const getUserRole = () => {
    if (isAdmin) return 'admin';
    if (isGroomer) return 'employee';
    return 'client';
  };

  // Handle both old (array) and new (object) navOrder formats
  const getCurrentNavOrder = () => {
    // If navOrder is an array (old format), use it directly
    if (Array.isArray(branding.navOrder)) {
      return branding.navOrder;
    }
    // If navOrder is an object (new format), get the role-specific order
    if (branding.navOrder && typeof branding.navOrder === 'object') {
      return branding.navOrder[getUserRole()] || branding.navOrder.client || branding.navOrder.admin;
    }
    // Fallback to default order
    return ['home', 'clients', 'appointments', 'availability', 'timeOff', 'recommendations', 'profile', 'users', 'messaging', 'quickBuy', 'inventory', 'aboutUs', 'contactUs', 'admin'];
  };

  const currentNavOrder = getCurrentNavOrder();

  const navLinks = currentNavOrder.map((key) => {
    const config = getNavItemConfig(key);
    if (!config || !config.show) return null;
    return { key, config };
  }).filter(Boolean);

  return (
    <nav className="shadow-lg relative" style={{ backgroundColor: branding.navbarBackgroundColor }} ref={menuRef}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-2">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ backgroundColor: branding.logoBackgroundColor }}
              >
                {branding.logoType === 'image' && branding.logoImage ? (
                  <img src={branding.logoImage} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span 
                    className="font-bold text-xl"
                    style={{ color: branding.logoTextColor, fontFamily: branding.logoFontFamily }}
                  >
                    {branding.siteIcon}
                  </span>
                )}
              </div>
              <span 
                className="text-xl font-bold whitespace-nowrap"
                style={{ color: branding.logoTextColor, fontFamily: branding.logoFontFamily }}
              >
                {branding.siteName}
              </span>
            </Link>

            {/* Horizontal nav — shown when forceHorizontal OR auto on large screens */}
            {!useHamburger || forceHorizontal ? (
              <div className="flex items-center space-x-1">
                {navLinks.map(({ key, config }) => {
                  const Icon = config.icon;
                  return (
                    <Link
                      key={key}
                      to={config.to}
                      className="flex items-center space-x-1 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                      style={getNavLabelStyle(key)}
                    >
                      <Icon size={20} />
                      <span>{config.label}</span>
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-2">
            {/* Hamburger button — shown in hamburger/auto mode */}
            {useHamburger && !forceHorizontal ? (
              <button
                onClick={() => setMenuOpen(o => !o)}
                className="flex items-center space-x-1 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                style={{ color: '#374151' }}
                aria-label="Toggle menu"
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
                <span className="text-sm font-medium">Menu</span>
              </button>
            ) : null}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 px-3 py-2 rounded-md text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Vertical dropdown menu */}
      {useHamburger && !forceHorizontal && menuOpen && (
        <div
          className="absolute right-0 top-full z-50 w-56 shadow-xl rounded-bl-lg border border-gray-200 py-1"
          style={{ backgroundColor: branding.navbarBackgroundColor }}
        >
          {navLinks.map(({ key, config }) => {
            const Icon = config.icon;
            return (
              <Link
                key={key}
                to={config.to}
                className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-100 transition-colors"
                style={getNavLabelStyle(key)}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{config.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
